import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Button,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  FileSpreadsheet,
  FolderGit2,
  Network,
  Minus,
  ScanLine,
  ShieldCheck,
  TrendingUp,
} from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import {
  getManagementOverview,
  getManagementTrends,
  ManagementOverview,
  ManagementTrends,
  ProjectScoreRow,
} from '@/lib/api/managementService';
import { exportManagementExcel, exportManagementPdf } from '@/lib/exportManagement';
import { SEVERITY_COLORS } from './components/dashboard-types';

const RAG_COLORS = { GREEN: '#22c55e', AMBER: '#f59e0b', RED: '#ef4444' } as const;
const NOT_SCANNED_COLOR = '#94a3b8';

const CARD_BORDER = (theme: any) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`;

function ragChipColor(rag: string | null) {
  return rag === 'GREEN' ? RAG_COLORS.GREEN : rag === 'AMBER' ? RAG_COLORS.AMBER : rag === 'RED' ? RAG_COLORS.RED : NOT_SCANNED_COLOR;
}

function ragLabel(rag: string | null, scanned: boolean) {
  if (!scanned) return 'Not scanned';
  return rag === 'GREEN' ? 'Healthy' : rag === 'AMBER' ? 'Needs Attention' : 'Critical';
}

function DeltaChip({ delta }: { delta: number | null }) {
  if (delta == null) return <Typography fontSize="0.7rem" color="text.secondary">—</Typography>;
  const up = delta > 0;
  const flat = delta === 0;
  const color = flat ? '#94a3b8' : up ? RAG_COLORS.GREEN : RAG_COLORS.RED;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  return (
    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ color }}>
      <Icon size={14} />
      <Typography fontSize="0.72rem" fontWeight={600}>{up ? '+' : ''}{delta}</Typography>
    </Stack>
  );
}

function KpiCard({ icon, label, value, sub, accent }: { icon: React.ReactNode; label: string; value: React.ReactNode; sub?: React.ReactNode; accent?: string }) {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ border: CARD_BORDER(theme), flex: 1, minWidth: 150 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: accent || 'text.secondary', mb: 0.75 }}>
          {icon}
          <Typography fontSize="0.68rem" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
        </Stack>
        <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.1 }}>{value}</Typography>
        {sub && <Box mt={0.5}>{sub}</Box>}
      </CardContent>
    </Card>
  );
}

function WidgetCard({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ border: CARD_BORDER(theme), height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 1.75, pb: 1 }}>
        <Typography fontWeight={700} fontSize="0.85rem">{title}</Typography>
        {action}
      </Stack>
      <Divider sx={{ opacity: 0.5 }} />
      <Box sx={{ p: 2, flex: 1, minHeight: 0 }}>{children}</Box>
    </Card>
  );
}

interface FilteredAgg {
  projects: ProjectScoreRow[];
  scanned: number;
  notScanned: number;
  green: number;
  amber: number;
  red: number;
  ns: number;
  avgScore: number | null;
  prevAvgScore: number | null;
  criticalFindings: number;
  improving: number;
  needsAttention: number;
  newThisMonth: number;
  categories: Record<string, { critical: number; high: number; medium: number; low: number; total: number }>;
  domains: Map<string, { projectCount: number; scoreSum: number; scoreCount: number; green: number; amber: number; red: number; notScanned: number; criticalFindings: number }>;
  topAttention: ProjectScoreRow[];
  toolCoverage: Record<string, number>;
}

function aggregate(projects: ProjectScoreRow[]): FilteredAgg {
  const agg: FilteredAgg = {
    projects, scanned: 0, notScanned: 0, green: 0, amber: 0, red: 0, ns: 0,
    avgScore: null, prevAvgScore: null, criticalFindings: 0, improving: 0,
    needsAttention: 0, newThisMonth: 0, categories: {}, domains: new Map(),
    topAttention: [], toolCoverage: {},
  };
  let scoreSum = 0, scoreCount = 0, prevSum = 0, prevCount = 0;
  const firstOfMonth = new Date();
  firstOfMonth.setUTCDate(1); firstOfMonth.setUTCHours(0, 0, 0, 0);

  for (const p of projects) {
    if (p.scanned) agg.scanned++; else agg.notScanned++;
    if (p.rag === 'GREEN') { agg.green++; } else if (p.rag === 'AMBER') { agg.amber++; }
    else if (p.rag === 'RED') { agg.red++; }
    if (!p.scanned) agg.ns++;
    if (p.score != null) { scoreSum += p.score; scoreCount++; }
    if (p.scoreDelta != null && p.score != null) { prevSum += p.score - p.scoreDelta; prevCount++; }
    if ((p.scoreDelta ?? 0) > 0) agg.improving++;
    agg.criticalFindings += p.criticalFindings;
    if (p.createdAt && new Date(p.createdAt) >= firstOfMonth) agg.newThisMonth++;
    for (const [key, cat] of Object.entries(p.findingsByCategory || {})) {
      const t = agg.categories[key] || (agg.categories[key] = { critical: 0, high: 0, medium: 0, low: 0, total: 0 });
      t.critical += cat.critical; t.high += cat.high; t.medium += cat.medium; t.low += cat.low; t.total += cat.total;
    }
    for (const tool of p.toolCoverage?.connected || []) {
      agg.toolCoverage[tool] = (agg.toolCoverage[tool] || 0) + 1;
    }
    const d = agg.domains.get(p.domain) || { projectCount: 0, scoreSum: 0, scoreCount: 0, green: 0, amber: 0, red: 0, notScanned: 0, criticalFindings: 0 };
    d.projectCount++;
    if (p.score != null) { d.scoreSum += p.score; d.scoreCount++; }
    if (p.rag === 'GREEN') d.green++; else if (p.rag === 'AMBER') d.amber++; else if (p.rag === 'RED') d.red++;
    if (!p.scanned) d.notScanned++;
    d.criticalFindings += p.criticalFindings;
    agg.domains.set(p.domain, d);
  }
  agg.avgScore = scoreCount ? Math.round(scoreSum / scoreCount) : null;
  agg.prevAvgScore = prevCount ? Math.round(prevSum / prevCount) : null;
  agg.needsAttention = agg.amber;
  agg.topAttention = [...projects]
    .filter((p) => p.scanned)
    .sort((a, b) => (a.score ?? 101) - (b.score ?? 101) || b.criticalFindings - a.criticalFindings)
    .slice(0, 10);
  return agg;
}

const CATEGORY_ORDER = [
  { key: 'codeQuality', label: 'Code Quality' },
  { key: 'openSource', label: 'Open Source' },
  { key: 'secrets', label: 'Secrets' },
  { key: 'container', label: 'Container' },
  { key: 'polaris', label: 'SAST (Polaris)' },
  { key: 'aiAnalysis', label: 'Deep AI' },
];

const ACTIVITY_META: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  onboarded: { label: 'Project onboarded', color: '#3b82f6', icon: FolderGit2 },
  scan: { label: 'Scan completed', color: '#22c55e', icon: ScanLine },
  score: { label: 'Score updated', color: '#8b5cf6', icon: TrendingUp },
};

export default function ManagementDashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [overview, setOverview] = useState<ManagementOverview | null>(null);
  const [trends, setTrends] = useState<ManagementTrends['trends']>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [domainFilter, setDomainFilter] = useState('__all__');
  const [ragFilter, setRagFilter] = useState('__all__');
  const [months, setMonths] = useState(6);
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getManagementOverview();
        if (!cancelled) setOverview(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load management overview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getManagementTrends(months);
        if (!cancelled) setTrends(data.trends || []);
      } catch { /* trends are non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, [months]);

  const domainOptions = useMemo(
    () => [...new Set((overview?.projects || []).map((p) => p.domain))].sort(),
    [overview],
  );

  const filtered = useMemo(() => {
    if (!overview) return null;
    let rows = overview.projects;
    if (domainFilter !== '__all__') rows = rows.filter((p) => p.domain === domainFilter);
    if (ragFilter === 'GREEN' || ragFilter === 'AMBER' || ragFilter === 'RED') {
      rows = rows.filter((p) => p.rag === ragFilter && p.scanned);
    } else if (ragFilter === 'NOT_SCANNED') {
      rows = rows.filter((p) => !p.scanned);
    }
    return aggregate(rows);
  }, [overview, domainFilter, ragFilter]);

  if (loading) {
    return (
      <Stack width="100%" p={4} alignItems="center" spacing={2}>
        <CircularProgress size={52} thickness={4} />
        <Typography color="text.secondary">Loading management overview…</Typography>
      </Stack>
    );
  }
  if (error || !overview || !filtered) {
    return (
      <Stack width="100%" p={4}>
        <Alert severity="error">{error || 'No data available'}</Alert>
      </Stack>
    );
  }

  const allTrend = trends.map((t) => ({ month: t.month, score: t.avgScore, critical: t.criticalFindings }));
  const firstDataIdx = allTrend.findIndex((t) => t.score != null || t.critical != null);
  const trendData = firstDataIdx < 0 ? [] : allTrend.slice(firstDataIdx);
  const healthPie = [
    { name: 'Healthy', value: filtered.green, color: RAG_COLORS.GREEN },
    { name: 'Needs Attention', value: filtered.amber, color: RAG_COLORS.AMBER },
    { name: 'Critical', value: filtered.red, color: RAG_COLORS.RED },
    { name: 'Not Scanned', value: filtered.ns, color: NOT_SCANNED_COLOR },
  ].filter((s) => s.value > 0);
  const categoryBars = CATEGORY_ORDER
    .map((c) => ({ name: c.label, ...(filtered.categories[c.key] || { critical: 0, high: 0, medium: 0, low: 0, total: 0 }) }))
    .filter((c) => c.total > 0);
  const domainRows = [...filtered.domains.entries()]
    .map(([domain, d]) => ({ domain, ...d, avgScore: d.scoreCount ? Math.round(d.scoreSum / d.scoreCount) : null }))
    .sort((a, b) => (a.avgScore ?? 101) - (b.avgScore ?? 101));

  const toggleDomain = (domain: string) =>
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain); else next.add(domain);
      return next;
    });

  return (
    <Stack width="100%" p={3} spacing={2.5}>
      {/* Header row */}
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
        <Box flex={1} minWidth={220}>
          <Typography variant="h5" fontWeight={700}>Security Overview</Typography>
          <Typography fontSize="0.8rem" color="text.secondary">
            {overview.scope === 'assigned'
              ? `Showing your ${overview.projects.length} assigned ${overview.projects.length === 1 ? 'project' : 'projects'}`
              : 'Organization security posture across all projects'} — updated {formatDistanceToNow(new Date(overview.generatedAt), { addSuffix: true })}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FolderGit2 size={16} />}
          onClick={() => navigate('/projects')}
          sx={{ textTransform: 'none', height: 40 }}
        >
          View Projects
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Network size={16} />}
          onClick={() => navigate('/management/hierarchy')}
          sx={{ textTransform: 'none', height: 40 }}
        >
          Hierarchy
        </Button>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Domain</InputLabel>
          <Select value={domainFilter} label="Domain" onChange={(e) => setDomainFilter(e.target.value)}>
            <MenuItem value="__all__">All domains</MenuItem>
            {domainOptions.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Health</InputLabel>
          <Select value={ragFilter} label="Health" onChange={(e) => setRagFilter(e.target.value)}>
            <MenuItem value="__all__">All statuses</MenuItem>
            <MenuItem value="GREEN">Healthy</MenuItem>
            <MenuItem value="AMBER">Needs Attention</MenuItem>
            <MenuItem value="RED">Critical</MenuItem>
            <MenuItem value="NOT_SCANNED">Not scanned</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Trend window</InputLabel>
          <Select value={months} label="Trend window" onChange={(e) => setMonths(Number(e.target.value))}>
            <MenuItem value={3}>Last 3 months</MenuItem>
            <MenuItem value={6}>Last 6 months</MenuItem>
            <MenuItem value={12}>Last 12 months</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Export project scorecard as Excel">
          <IconButton onClick={() => exportManagementExcel(filtered.projects)} sx={{ border: CARD_BORDER(theme) }}>
            <FileSpreadsheet size={18} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Export executive report as PDF">
          <IconButton onClick={() => exportManagementPdf(overview, domainRows, filtered.topAttention)} sx={{ border: CARD_BORDER(theme) }}>
            <Download size={18} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* KPI cards */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <KpiCard
          icon={<ShieldCheck size={16} />}
          label="Overall Score"
          accent="#6366f1"
          value={filtered.avgScore != null ? `${filtered.avgScore}/100` : '—'}
          sub={filtered.prevAvgScore != null && filtered.avgScore != null
            ? <DeltaChip delta={filtered.avgScore - filtered.prevAvgScore} />
            : undefined}
        />
        <KpiCard
          icon={<FolderGit2 size={16} />}
          label="Total Projects"
          accent="#3b82f6"
          value={filtered.projects.length}
          sub={<Typography fontSize="0.7rem" color="text.secondary">+{filtered.newThisMonth} new this month</Typography>}
        />
        <KpiCard
          icon={<AlertTriangle size={16} />}
          label="Critical Projects"
          accent={RAG_COLORS.RED}
          value={filtered.red}
          sub={<Typography fontSize="0.7rem" color="text.secondary">{filtered.amber} need attention</Typography>}
        />
        <KpiCard
          icon={<BarChart3 size={16} />}
          label="Critical Findings"
          accent={RAG_COLORS.RED}
          value={filtered.criticalFindings}
          sub={<Typography fontSize="0.7rem" color="text.secondary">across all tools</Typography>}
        />
        <KpiCard
          icon={<TrendingUp size={16} />}
          label="Improving"
          accent={RAG_COLORS.GREEN}
          value={filtered.improving}
          sub={<Typography fontSize="0.7rem" color="text.secondary">score up since last scan</Typography>}
        />
        <KpiCard
          icon={<Clock size={16} />}
          label="Needs Attention"
          accent={RAG_COLORS.AMBER}
          value={filtered.needsAttention}
          sub={<Typography fontSize="0.7rem" color="text.secondary">{filtered.ns} not scanned</Typography>}
        />
      </Stack>

      {/* Row: trend + health + categories */}
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
        <Box flex={1.4} minWidth={0}>
          <WidgetCard title="Security Posture Trend">
            {trendData.length ? (
              <ResponsiveContainer width="100%" height={230}>
                <LineChart data={trendData} margin={{ top: 8, right: 8, left: 4, bottom: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.08)} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }}>
                    <Label value="Month" position="insideBottom" offset={-10} style={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                  </XAxis>
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }}>
                    <Label value="Average score (0-100)" angle={-90} position="insideLeft" style={{ fontSize: 11, fill: theme.palette.text.secondary, textAnchor: 'middle' }} />
                  </YAxis>
                  <ReTooltip />
                  <Legend verticalAlign="top" height={24} wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="score" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} name="Avg score" connectNulls />
                </LineChart>
              </ResponsiveContainer>
            ) : <Typography fontSize="0.8rem" color="text.secondary">No scan history yet.</Typography>}
          </WidgetCard>
        </Box>
        <Box flex={1} minWidth={0}>
          <WidgetCard title="Project Health">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box width={160} height={180}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={healthPie} dataKey="value" innerRadius={52} outerRadius={75} paddingAngle={2} strokeWidth={0}>
                      {healthPie.map((s) => <Cell key={s.name} fill={s.color} />)}
                    </Pie>
                    <ReTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Stack spacing={0.75} flex={1}>
                {healthPie.map((s) => (
                  <Stack key={s.name} direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: s.color }} />
                    <Typography fontSize="0.75rem" flex={1}>{s.name}</Typography>
                    <Typography fontSize="0.78rem" fontWeight={700}>{s.value}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </WidgetCard>
        </Box>
        <Box flex={1.2} minWidth={0}>
          <WidgetCard title="Findings by Category">
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={categoryBars} layout="vertical" margin={{ top: 0, right: 8, left: 4, bottom: 20 }}>
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false}>
                  <Label value="Number of findings" position="insideBottom" offset={-10} style={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                </XAxis>
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }}>
                  <Label value="Scan tool / category" angle={-90} position="insideLeft" style={{ fontSize: 11, fill: theme.palette.text.secondary, textAnchor: 'middle' }} />
                </YAxis>
                <ReTooltip />
                <Legend verticalAlign="top" height={24} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="critical" stackId="a" fill={SEVERITY_COLORS.critical} name="Critical" />
                <Bar dataKey="high" stackId="a" fill={SEVERITY_COLORS.high} name="High" />
                <Bar dataKey="medium" stackId="a" fill={SEVERITY_COLORS.medium} name="Medium" />
                <Bar dataKey="low" stackId="a" fill={SEVERITY_COLORS.low} name="Low" />
              </BarChart>
            </ResponsiveContainer>
          </WidgetCard>
        </Box>
      </Stack>

      {/* Row: domain table + top attention */}
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
        <Box flex={1.4} minWidth={0}>
          <WidgetCard title="Security Posture by Domain">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Domain</TableCell>
                    <TableCell align="center">Score</TableCell>
                    <TableCell align="center">Projects</TableCell>
                    <TableCell align="center">Health</TableCell>
                    <TableCell align="right">Crit. findings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {domainRows.map((d) => {
                    const expanded = expandedDomains.has(d.domain);
                    const domainProjects = filtered.projects.filter((p) => p.domain === d.domain);
                    return (
                      <Fragment key={d.domain}>
                        <TableRow hover onClick={() => toggleDomain(d.domain)} sx={{ cursor: 'pointer' }}>
                          <TableCell width={28} sx={{ pr: 0 }}>
                            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{d.domain}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={d.avgScore ?? '—'}
                              size="small"
                              sx={{
                                fontWeight: 700, minWidth: 40,
                                color: d.avgScore == null ? 'text.secondary' : ragChipColor(d.avgScore >= 75 ? 'GREEN' : d.avgScore >= 50 ? 'AMBER' : 'RED'),
                                borderColor: 'divider', border: '1px solid',
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">{d.projectCount}</TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              {d.green > 0 && <Chip size="small" label={d.green} sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha(RAG_COLORS.GREEN, 0.15), color: RAG_COLORS.GREEN }} />}
                              {d.amber > 0 && <Chip size="small" label={d.amber} sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha(RAG_COLORS.AMBER, 0.15), color: RAG_COLORS.AMBER }} />}
                              {d.red > 0 && <Chip size="small" label={d.red} sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha(RAG_COLORS.RED, 0.15), color: RAG_COLORS.RED }} />}
                              {d.notScanned > 0 && <Chip size="small" label={d.notScanned} sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha(NOT_SCANNED_COLOR, 0.15), color: NOT_SCANNED_COLOR }} />}
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{d.criticalFindings}</TableCell>
                        </TableRow>
                        {expanded && domainProjects.map((p) => (
                          <TableRow
                            key={p.appId}
                            hover
                            onClick={() => navigate(`/projects/${p.appId}`)}
                            sx={{ cursor: 'pointer', bgcolor: alpha(theme.palette.text.primary, 0.02) }}
                          >
                            <TableCell />
                            <TableCell>
                              <Typography fontSize="0.8rem">{p.appName}</Typography>
                              {p.leanIXId && <Typography fontSize="0.65rem" color="text.secondary">{p.leanIXId}</Typography>}
                            </TableCell>
                            <TableCell align="center">
                              <Typography fontSize="0.8rem" fontWeight={700} sx={{ color: ragChipColor(p.rag) }}>
                                {p.score ?? '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography fontSize="0.72rem" sx={{ color: ragChipColor(p.rag) }}>{ragLabel(p.rag, p.scanned)}</Typography>
                            </TableCell>
                            <TableCell align="center"><DeltaChip delta={p.scoreDelta} /></TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{p.criticalFindings}</TableCell>
                          </TableRow>
                        ))}
                      </Fragment>
                    );
                  })}
                  {domainRows.length === 0 && (
                    <TableRow><TableCell colSpan={6}><Typography fontSize="0.8rem" color="text.secondary" align="center" py={2}>No projects match the filters.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </WidgetCard>
        </Box>
        <Box flex={1} minWidth={0}>
          <WidgetCard title="Top Projects Requiring Attention">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Project</TableCell>
                    <TableCell>Domain</TableCell>
                    <TableCell align="center">Score</TableCell>
                    <TableCell align="center">Critical</TableCell>
                    <TableCell align="center">Trend</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.topAttention.map((p: ProjectScoreRow) => (
                    <TableRow
                      key={p.appId}
                      hover
                      onClick={() => navigate(`/projects/${p.appId}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem', maxWidth: 150 }}>
                        <Typography fontSize="0.78rem" fontWeight={600} noWrap>{p.appName}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', textTransform: 'capitalize' }}>{p.domain}</TableCell>
                      <TableCell align="center">
                        <Typography fontSize="0.8rem" fontWeight={700} sx={{ color: ragChipColor(p.rag) }}>{p.score ?? '—'}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.78rem' }}>{p.criticalFindings}</TableCell>
                      <TableCell align="center"><DeltaChip delta={p.scoreDelta} /></TableCell>
                      <TableCell width={28}><ArrowRight size={14} /></TableCell>
                    </TableRow>
                  ))}
                  {filtered.topAttention.length === 0 && (
                    <TableRow><TableCell colSpan={6}><Typography fontSize="0.8rem" color="text.secondary" align="center" py={2}>No scanned projects.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </WidgetCard>
        </Box>
      </Stack>

      {/* Row: scan status + critical trend + activity */}
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
        <Box flex={0.9} minWidth={0}>
          <WidgetCard title="Scan Coverage">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box width={140} height={150}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Scanned', value: filtered.scanned, color: RAG_COLORS.GREEN },
                        { name: 'Not scanned', value: filtered.notScanned, color: NOT_SCANNED_COLOR },
                      ].filter((s) => s.value > 0)}
                      dataKey="value" innerRadius={44} outerRadius={65} paddingAngle={2} strokeWidth={0}
                    >
                      {[{ name: 'Scanned', color: RAG_COLORS.GREEN }, { name: 'Not scanned', color: NOT_SCANNED_COLOR }].map((s) => <Cell key={s.name} fill={s.color} />)}
                    </Pie>
                    <ReTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Stack spacing={0.5} flex={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: RAG_COLORS.GREEN }} />
                  <Typography fontSize="0.72rem" flex={1}>Scanned</Typography>
                  <Typography fontSize="0.75rem" fontWeight={700}>{filtered.scanned}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: NOT_SCANNED_COLOR }} />
                  <Typography fontSize="0.72rem" flex={1}>Not scanned</Typography>
                  <Typography fontSize="0.75rem" fontWeight={700}>{filtered.notScanned}</Typography>
                </Stack>
                <Divider sx={{ my: 0.5 }} />
                {Object.entries(filtered.toolCoverage).sort((a, b) => b[1] - a[1]).map(([tool, count]) => (
                  <Stack key={tool} direction="row" spacing={1} alignItems="center">
                    <Typography fontSize="0.7rem" flex={1} sx={{ textTransform: 'capitalize' }}>{tool.replace('_', ' ')}</Typography>
                    <Typography fontSize="0.72rem" fontWeight={600}>{count}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </WidgetCard>
        </Box>
        <Box flex={1.1} minWidth={0}>
          <WidgetCard title="Critical Findings Trend">
            {trendData.length ? (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={trendData} margin={{ top: 8, right: 8, left: 4, bottom: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.08)} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }}>
                    <Label value="Month" position="insideBottom" offset={-10} style={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                  </XAxis>
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false}>
                    <Label value="Critical findings" angle={-90} position="insideLeft" style={{ fontSize: 11, fill: theme.palette.text.secondary, textAnchor: 'middle' }} />
                  </YAxis>
                  <ReTooltip />
                  <Legend verticalAlign="top" height={24} wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="critical" stroke={RAG_COLORS.RED} strokeWidth={2} dot={{ r: 3 }} name="Critical findings" />
                </LineChart>
              </ResponsiveContainer>
            ) : <Typography fontSize="0.8rem" color="text.secondary">No scan history yet.</Typography>}
          </WidgetCard>
        </Box>
        <Box flex={1} minWidth={0}>
          <WidgetCard title="Recent Activity">
            <Stack spacing={1}>
              {overview.activity.slice(0, 8).map((a, i) => {
                const meta = ACTIVITY_META[a.type] || ACTIVITY_META.scan;
                const Icon = meta.icon;
                return (
                  <Stack key={i} direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: meta.color, flexShrink: 0 }} />
                    <Box flex={1} minWidth={0}>
                      <Typography fontSize="0.75rem" noWrap>
                        {meta.label} — <strong>{a.appName}</strong>
                      </Typography>
                    </Box>
                    <Typography fontSize="0.68rem" color="text.secondary" flexShrink={0}>
                      {formatDistanceToNow(new Date(a.at), { addSuffix: true })}
                    </Typography>
                  </Stack>
                );
              })}
              {overview.activity.length === 0 && (
                <Typography fontSize="0.8rem" color="text.secondary">No recent activity.</Typography>
              )}
            </Stack>
          </WidgetCard>
        </Box>
      </Stack>

      {/* Full project scorecard table */}
      <WidgetCard
        title="All Projects"
        action={
          <Chip label={`${filtered.projects.length} projects`} size="small" variant="outlined" sx={{ fontSize: '0.68rem' }} />
        }
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Project</TableCell>
                <TableCell>Domain</TableCell>
                {overview.scope === 'assigned' && <TableCell align="center">Management level</TableCell>}
                <TableCell align="center">Criticality</TableCell>
                <TableCell align="center">Score</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Trend</TableCell>
                <TableCell align="right">Critical</TableCell>
                <TableCell align="center">Tools</TableCell>
                <TableCell align="right">Last scan</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {[...filtered.projects]
                .sort((a, b) => (a.score ?? 101) - (b.score ?? 101))
                .map((p) => (
                  <TableRow key={p.appId} hover onClick={() => navigate(`/projects/${p.appId}`)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Typography fontSize="0.8rem" fontWeight={600}>{p.appName}</Typography>
                      {p.leanIXId && <Typography fontSize="0.65rem" color="text.secondary">{p.leanIXId}</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>{p.domain}</TableCell>
                    {overview.scope === 'assigned' && (
                      <TableCell align="center">
                        {p.managementLevel ? <Chip label={p.managementLevel} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} /> : '—'}
                      </TableCell>
                    )}
                    <TableCell align="center" sx={{ fontSize: '0.72rem', textTransform: 'capitalize' }}>{p.criticality || '—'}</TableCell>
                    <TableCell align="center">
                      <Typography fontSize="0.82rem" fontWeight={700} sx={{ color: ragChipColor(p.rag) }}>{p.score ?? '—'}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={ragLabel(p.rag, p.scanned)}
                        size="small"
                        sx={{
                          height: 20, fontSize: '0.65rem', fontWeight: 600,
                          bgcolor: alpha(ragChipColor(p.rag), 0.12), color: ragChipColor(p.rag),
                        }}
                      />
                    </TableCell>
                    <TableCell align="center"><DeltaChip delta={p.scoreDelta} /></TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{p.criticalFindings}</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.72rem' }}>
                      {p.toolCoverage ? `${p.toolCoverage.connected.length} connected` : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      {p.lastScanAt ? formatDistanceToNow(new Date(p.lastScanAt), { addSuffix: true }) : '—'}
                    </TableCell>
                    <TableCell width={28}><ArrowRight size={14} /></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </WidgetCard>
    </Stack>
  );
}
